import { Directive, effect, inject, input, TemplateRef, ViewContainerRef } from '@angular/core';

import { AuthService } from '@auth/services/auth.service';

/**
 * @description
 * This directive is used to conditionally render an element based on the user's permissions.
 * It checks if the user has the required permission to access a certain feature or view.
 *
 * @example
 * ```html
 * <div *appCanAccess="'permissionName'">
 *   <!-- Content to be displayed if the user has the 'permissionName' permission -->
 * </div>
 * ```
 *
 * @example
 * ```html
 * <div *appCanAccess="['permissionName', menuItemId]">
 *  <!-- Content to be displayed if the user has the 'permissionName' permission for a specific menu item -->
 * </div>
 * ```
 */
@Directive({
  selector: '[appCanAccess]',
  standalone: true
})
export class CanAccess {
  /**
   * The permission or permissions required to access the element.
   * It can be a string representing a single permission or an array where the first element
   * is the permission and the second is the menu item ID.
   * @type {string | [string, number]}
   */
  params = input.required<string | [string, number]>({ alias: 'appCanAccess' });

  private _authService = inject(AuthService);
  private _tempolateRef = inject(TemplateRef<any>);
  private _vcr = inject(ViewContainerRef);

  constructor() {
    effect(() => this._checkAccess());
  }

  /**
   * @private
   * Checks if the user has the required permission and renders the element accordingly.
   */
  private _checkAccess(): void {
    this._vcr.clear();
    const rawParams = this.params();
    const [permission, menuItemId] = Array.isArray(rawParams) ? rawParams : [rawParams, undefined];

    if (this._authService.hasPermission(permission, menuItemId)) {
      this._vcr.createEmbeddedView(this._tempolateRef);
    }
  }
}
